// Empty string = relative URL → axios uses the current page origin (and
// scheme). This lets the app work over plain http while nginx has no TLS,
// over https once a cert is installed, and in local dev — all without
// rebuilding. nginx proxies /api/* to the backend on :3002.
export const API_BASE = '';

/**
 * Parse an operator-entered pit time into seconds. Accepts either:
 *   - plain seconds, with optional decimals: "175" | "175.5" | "0.25"
 *   - minutes:seconds: "2:55" | "2:5" | "0:30" | "2:55.2"
 * Returns NaN for anything it can't parse. Used by both EventCard and the
 * LiveBoard End-Pit dialog so typing "2:55" is the same as typing "175".
 */
export function parsePitTimeSeconds(input: string): number {
  const s = input.trim();
  if (!s) return NaN;
  if (s.includes(':')) {
    const parts = s.split(':');
    if (parts.length !== 2) return NaN;
    const minutes = parseInt(parts[0], 10);
    const seconds = parseFloat(parts[1]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return NaN;
    if (minutes < 0 || seconds < 0 || seconds >= 60) return NaN;
    return minutes * 60 + seconds;
  }
  const v = parseFloat(s);
  return Number.isFinite(v) && v >= 0 ? v : NaN;
}

export type PitType = 'DC' | 'TIRE' | 'MECH_BF' | 'OTHER' | 'CHARGED';

export type PenaltyType =
  | 'CONE'
  | 'OFF_COURSE'
  | 'MISSED_SLALOM'
  | 'FLAG_DISOBEY'
  | 'OVER_DRIVING'
  | 'CONTACT_DQ'
  | 'RUN_OUT_OF_ORDER'
  | 'EXCESS_DC_TIME'
  | 'EXCESS_TIRE_TIME'
  | 'EXTRA_PERSON'
  | 'ENERGY_VIOLATION'
  | 'OTHER';

export interface LiveBoardRow {
  CarNumber: number;
  TeamName: string | null;
  Institution: string;
  VehicleClass: 'H' | 'E' | null;
  Status: string | null;
  StartTime: string | null;
  EndTime: string | null;
  TotalLaps: number;
  OnTrackLaps: number;
  DCLaps: number;
  PitLaps: number;
  PenaltyCount: number;
  // Hardware identity. Populated by get_all_cars_board.sql and
  // get_all_cars_board_for_car.sql (the "All Cars" / broadcast queries).
  // The on-track live board SQL does not select these, so consumers that
  // only subscribe to 'liveBoard' should treat them as absent.
  UID?: string | null;
  Transponder?: string | null;
}

export interface PenaltyRow {
  Recnum: number;
  CarNumber: number;
  TeamName: string | null;
  Institution: string;
  PenaltyType: PenaltyType;
  LapNumber: number | null;
  Description: string | null;
  TimePenalty: string;
  PointPenalty: string;
  CreatedAt: string;
  CreatedBy: string | null;
}

export interface PitRow {
  Recnum: number;
  CarNumber: number;
  TeamName: string | null;
  Institution: string;
  LapRecnum: number | null;
  PitType: PitType;
  PitNumber: number | null;
  StartTime: string;
  EndTime: string | null; // null => pit is still open (car is in pit)
  PitTime: string;        // server-computed (EndTime - StartTime, in seconds)
  IsFree: number | boolean;
  ExcessTime: string;
  RestartTime: string | null;
  RestartExceeded: number | boolean | null;
  CreatedAt: string;
}

// Shape of messages on the 'liveBoard' websocket channel.
export interface LiveBoardWsMessage {
  CarNumber: number;
  row: LiveBoardRow | null;
}

// Shape of messages on the 'pit' / 'penalty' websocket channels.
export type PitWsMessage =
  | { action: 'upsert'; row: PitRow }
  | { action: 'delete'; Recnum: number; CarNumber: number };

export type PenaltyWsMessage =
  | { action: 'upsert'; row: PenaltyRow }
  | { action: 'delete'; Recnum: number; CarNumber: number };
